<div class="suppliers-dashboard-container">

  <!-- Toast per notifiche -->
  <p-toast position="top-right" [life]="5000" role="status" aria-live="polite" aria-atomic="true"></p-toast>

  <!-- Breadcrumb -->
  <nav aria-label="Navigazione breadcrumb">
    <p-breadcrumb
      [model]="breadcrumbItems()"
      [home]="{ icon: 'pi pi-home', routerLink: '/axa', ariaLabel: 'Home' }"
      styleClass="breadcrumb-nav">
    </p-breadcrumb>
  </nav>

  <main class="dashboard-content">

    <!-- ── Header ─────────────────────────────────────────── -->
    <header class="page-header">
      <div class="header-content">
        <i class="pi pi-building header-icon" aria-hidden="true"></i>
        <h1 id="page-title">Dashboard Fornitori</h1>
      </div>
      <p class="header-description">
        Panoramica sui fornitori operativi, stato dei contratti e spesa aggregata.
        Usa i filtri globali per segmentare l'analisi per azienda, referente o tipo di servizio.
      </p>
    </header>

    <!-- ── Filtri globali ──────────────────────────────────── -->
    <section class="filters-section" aria-labelledby="filters-title">
      <h2 id="filters-title" class="section-title">
        <i class="pi pi-filter" aria-hidden="true"></i>
        Filtri globali
      </h2>

      <div class="filters-grid">

        <!-- Aziende -->
        <div class="filter-field">
          <label for="filter-companies" class="filter-label">Aziende coinvolte</label>
          @if (loadingFilters()) {
            <p-skeleton height="2.5rem" styleClass="w-full"></p-skeleton>
          } @else {
            <p-multiselect
              inputId="filter-companies"
              [options]="companyOptions()"
              [ngModel]="selectedCompanies()"
              (ngModelChange)="selectedCompanies.set($event); onFiltersChange()"
              optionLabel="valueDescription"
              optionValue="valueKey"
              placeholder="Tutte le aziende"
              class="w-full"
              [maxSelectedLabels]="2"
              selectedItemsLabel="{0} selezionate"
              [attr.aria-label]="'Filtro aziende coinvolte'"
              [attr.aria-multiselectable]="true">
            </p-multiselect>
          }
        </div>

        <!-- Referenti -->
        <div class="filter-field">
          <label for="filter-referents" class="filter-label">Referente AXA / Buyer</label>
          @if (loadingFilters()) {
            <p-skeleton height="2.5rem" styleClass="w-full"></p-skeleton>
          } @else {
            <p-multiselect
              inputId="filter-referents"
              [options]="referentOptions()"
              [ngModel]="selectedReferents()"
              (ngModelChange)="selectedReferents.set($event); onFiltersChange()"
              placeholder="Tutti i referenti"
              class="w-full"
              [maxSelectedLabels]="2"
              selectedItemsLabel="{0} selezionati"
              [attr.aria-label]="'Filtro referenti AXA'"
              [attr.aria-multiselectable]="true">
            </p-multiselect>
          }
        </div>

        <!-- Tipo di servizio -->
        <div class="filter-field">
          <label for="filter-service-types" class="filter-label">Tipo di servizio</label>
          @if (loadingFilters()) {
            <p-skeleton height="2.5rem" styleClass="w-full"></p-skeleton>
          } @else {
            <p-multiselect
              inputId="filter-service-types"
              [options]="serviceTypeOptions()"
              [ngModel]="selectedServiceTypes()"
              (ngModelChange)="selectedServiceTypes.set($event); onFiltersChange()"
              optionLabel="valueDescription"
              optionValue="valueKey"
              placeholder="Tutti i tipi"
              class="w-full"
              [maxSelectedLabels]="2"
              selectedItemsLabel="{0} selezionati"
              [attr.aria-label]="'Filtro tipo di servizio'"
              [attr.aria-multiselectable]="true">
            </p-multiselect>
          }
        </div>

        <!-- Reset -->
        <div class="filter-reset">
          <button
            pButton
            type="button"
            label="Azzera filtri"
            icon="pi pi-times"
            iconPos="left"
            severity="secondary"
            [outlined]="true"
            (click)="resetFilters()"
            [disabled]="!selectedCompanies().length && !selectedReferents().length && !selectedServiceTypes().length">
          </button>
        </div>

      </div>
    </section>

    <!-- ── KPI cards (stato contratti) ────────────────────── -->
    <section class="kpi-section" aria-labelledby="kpi-title">
      <h2 id="kpi-title" class="visually-hidden">Riepilogo stato contratti</h2>

      @if (loadingSummary()) {
        <div class="kpi-cards" role="list" aria-label="Caricamento riepilogo contratti" aria-busy="true">
          @for (i of [1,2,3,4,5,6]; track i) {
            <div class="kpi-card" role="listitem" aria-hidden="true">
              <p-skeleton height="1rem" width="60%" styleClass="mb-2"></p-skeleton>
              <p-skeleton height="2rem" width="40%"></p-skeleton>
            </div>
          }
        </div>
      } @else {
        <div class="kpi-cards">
          @for (card of kpiCards(); track card.label) {
            <button
              type="button"
              class="kpi-card"
              [ngClass]="card.styleClass"
              (click)="filterContractsByStatus(card.statuses)"
              [attr.aria-label]="card.label + ' ' + card.value + ' - Apri il registro contratti filtrato'">
              <span class="kpi-label">{{ card.label }}</span>
              <span class="kpi-value">{{ card.value.toLocaleString('it-IT') }}</span>
            </button>
          }
        </div>
      }
    </section>

    <!-- ── Widget grafici ─────────────────────────────────── -->
    <section class="widgets-section" aria-labelledby="widgets-title">
      <h2 id="widgets-title" class="visually-hidden">Widget analisi fornitori</h2>

      <div class="widgets-grid">

        <!-- Widget 1: Fornitori per Azienda (barre orizzontali) -->
        <article class="widget-card" aria-labelledby="chart-company-title">
          <header class="widget-header">
            <h3 class="widget-title" id="chart-company-title">
              <i class="pi pi-chart-bar" aria-hidden="true"></i>
              Fornitori per Azienda
            </h3>
            <p class="widget-desc">Fornitori operativi raggruppati per azienda AXA coinvolta</p>
          </header>

          @if (loadingSummary()) {
            <p-skeleton height="220px"></p-skeleton>
          } @else if (suppliersByCompanyChartData()) {
            <div
              class="chart-content"
              role="img"
              [attr.aria-labelledby]="'chart-company-title'"
              [attr.aria-describedby]="'chart-company-data'">
              <div
                class="chart-interactive-wrapper"
                tabindex="0"
                role="button"
                aria-label="Grafico fornitori per azienda. Premi Invio per vedere la lista completa dei fornitori."
                (keydown.enter)="showAllCompaniesModal()"
                (keydown.space)="showAllCompaniesModal(); $event.preventDefault()">
                <p-chart
                  type="bar"
                  [data]="suppliersByCompanyChartData()"
                  [options]="suppliersByCompanyChartOptions()"
                  [style]="{ height: '220px' }"
                  (onDataSelect)="onCompanyBarClick($event)">
                </p-chart>
              </div>
            </div>
            <!-- WCAG 1.3.1: dati testuali per screen reader -->
            <div id="chart-company-data" class="visually-hidden">
              <p>Fornitori operativi per azienda:</p>
              <ul>
                @for (item of summary()?.suppliersByCompany ?? []; track item.company) {
                  <li>{{ getCompanyLabel(item.company) }}: {{ item.suppliersCount }} fornitori</li>
                }
              </ul>
            </div>
          } @else {
            <div class="empty-state" role="status">
              <i class="pi pi-inbox" aria-hidden="true"></i>
              <p>Nessun dato disponibile</p>
            </div>
          }
        </article>

        <!-- Widget 2: Spesa per Fornitore (barre orizzontali) -->
        <article class="widget-card" aria-labelledby="chart-spending-title">
          <header class="widget-header">
            <h3 class="widget-title" id="chart-spending-title">
              <i class="pi pi-euro" aria-hidden="true"></i>
              Top 10 Fornitori per Spesa
            </h3>
            <p class="widget-desc">Valore totale contratti operativi per fornitore — Top 10</p>
          </header>

          @if (loadingSummary()) {
            <p-skeleton height="300px"></p-skeleton>
          } @else if (spendingChartData()) {
            <div
              class="chart-content"
              role="img"
              [attr.aria-labelledby]="'chart-spending-title'"
              [attr.aria-describedby]="'chart-spending-data'">
              <p-chart
                type="bar"
                [data]="spendingChartData()"
                [options]="spendingChartOptions()"
                [style]="{ height: '300px' }">
              </p-chart>
            </div>
            <div id="chart-spending-data" class="visually-hidden">
              <p>Spesa per fornitore:</p>
              <ul>
                @for (item of summary()?.spendingBySupplier ?? []; track item.supplierId) {
                  <li>{{ item.supplierName }}: {{ formatCurrency(item.totalContractValue, item.currency) }}</li>
                }
              </ul>
            </div>
          } @else {
            <div class="empty-state" role="status">
              <i class="pi pi-inbox" aria-hidden="true"></i>
              <p>Nessun dato disponibile</p>
            </div>
          }
        </article>

        <!-- Widget 3: Stato Contratti (ciambella) -->
        <article class="widget-card" aria-labelledby="chart-status-title">
          <header class="widget-header">
            <h3 class="widget-title" id="chart-status-title">
              <i class="pi pi-chart-pie" aria-hidden="true"></i>
              Stato Contratti
            </h3>
            <p class="widget-desc">Distribuzione di tutti i contratti fornitori per stato (vista portfolio completa)</p>
          </header>

          @if (loadingSummary()) {
            <p-skeleton height="220px"></p-skeleton>
          } @else if (contractsStatusChartData()) {
            <div
              class="chart-content"
              role="img"
              [attr.aria-labelledby]="'chart-status-title'"
              [attr.aria-describedby]="'chart-status-data'">
              <p-chart
                type="doughnut"
                [data]="contractsStatusChartData()"
                [options]="contractsStatusChartOptions()"
                [style]="{ width: '100%' }">
              </p-chart>
            </div>
            <div id="chart-status-data" class="visually-hidden">
              <p>Distribuzione contratti per stato (totale: {{ summary()?.contractsStatus?.total ?? 0 }}):</p>
              <ul>
                @for (card of kpiCards(); track card.label) {
                  <li>{{ card.label }}: {{ card.value }}</li>
                }
              </ul>
            </div>
          } @else {
            <div class="empty-state" role="status">
              <i class="pi pi-inbox" aria-hidden="true"></i>
              <p>Nessun dato disponibile</p>
            </div>
          }
        </article>

        <!-- Widget 4: Fornitori per Tipo Servizio (tabella con tag) -->
        <article class="widget-card" aria-labelledby="table-service-title">
          <header class="widget-header">
            <h3 class="widget-title" id="table-service-title">
              <i class="pi pi-tags" aria-hidden="true"></i>
              Fornitori per Tipo di Servizio
            </h3>
            <p class="widget-desc">Fornitori distinti con contratti operativi raggruppati per tipo di società</p>
          </header>

          @if (loadingSummary()) {
            <div>
              @for (i of [1,2,3,4]; track i) {
                <p-skeleton height="2.5rem" styleClass="mb-2"></p-skeleton>
              }
            </div>
          } @else {
            <p-table
              [value]="summary()?.suppliersByService ?? []"
              [tableStyle]="{ 'min-width': '100%' }"
              styleClass="p-datatable-sm"
              aria-label="Fornitori per tipo di servizio">

              <ng-template pTemplate="header">
                <tr>
                  <th scope="col">Tipo di Servizio</th>
                  <th scope="col" class="text-center" style="width:5rem">N.</th>
                  <th scope="col">Fornitori</th>
                </tr>
              </ng-template>

              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>
                    <span class="service-type-label">{{ row.serviceTypeLabel || row.serviceTypeId }}</span>
                  </td>
                  <td class="text-center">
                    <p-tag [value]="row.suppliersCount.toString()" severity="info"></p-tag>
                  </td>
                  <td>
                    <div class="supplier-tags">
                      @for (s of row.suppliers.slice(0, 4); track s.supplierId) {
                        <button
                          type="button"
                          class="supplier-pill"
                          (click)="navigateToSupplier(s.supplierId)"
                          [pTooltip]="'Vai al fornitore ' + s.supplierName"
                          [attr.aria-label]="'Naviga al fornitore ' + s.supplierName">
                          {{ s.supplierName }}
                        </button>
                      }
                      @if (row.suppliers.length > 4) {
                        <span
                          class="supplier-pill supplier-pill--more"
                          [pTooltip]="getExtraSupplierNames(row.suppliers, 4)"
                          [attr.aria-label]="'e altri ' + (row.suppliers.length - 4) + ' fornitori'">
                          +{{ row.suppliers.length - 4 }} altri
                        </span>
                      }
                    </div>
                  </td>
                </tr>
              </ng-template>

              <ng-template pTemplate="emptymessage">
                <tr>
                  <td colspan="3" class="text-center">
                    <div class="empty-state" role="status">
                      <i class="pi pi-inbox" aria-hidden="true"></i>
                      <p>Nessun dato disponibile</p>
                    </div>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          }
        </article>

      </div>
    </section>

    <!-- ── Widget Attività pendenti ────────────────────────── -->
    <section class="activities-section" aria-labelledby="activities-title">
      <header class="section-header">
        <h2 id="activities-title" class="section-title">
          <i class="pi pi-list-check" aria-hidden="true"></i>
          Attività pendenti
        </h2>
        <p class="section-desc">
          Contratti fornitore in bozza, in rinnovo o in scadenza che richiedono attenzione.
          <strong>Totale: {{ activitiesTotal().toLocaleString('it-IT') }}</strong>
        </p>
      </header>

      <p-table
        [value]="activities()"
        [lazy]="true"
        [paginator]="true"
        [rows]="activitiesSize()"
        [totalRecords]="activitiesTotal()"
        [rowsPerPageOptions]="[10, 25, 50]"
        [loading]="loadingActivities()"
        (onPage)="onActivitiesPage($event)"
        [tableStyle]="{ 'min-width': '900px' }"
        styleClass="p-datatable-sm p-datatable-striped activities-table"
        aria-label="Attività pendenti sui contratti fornitori"
        aria-describedby="activities-title">

        <ng-template pTemplate="header">
          <tr>
            <th scope="col" style="width:8rem">Stato</th>
            <th scope="col">Tipo Servizio</th>
            <th scope="col">Oggetto Contratto</th>
            <th scope="col" style="width:9rem">Data Fine</th>
            <th scope="col" style="width:8rem" class="text-center">Gg Rimanenti</th>
            <th scope="col" style="width:8rem" class="text-center">Preavviso</th>
            <th scope="col" style="width:4rem" class="text-center">Azioni</th>
          </tr>
        </ng-template>

        <ng-template pTemplate="body" let-contract>
          <tr>
            <td>
              <p-tag
                [value]="getStatusLabel(contract.status)"
                [severity]="getStatusSeverity(contract.status)">
              </p-tag>
            </td>
            <td>{{ contract.serviceType || '—' }}</td>
            <td>
              <span
                class="contract-object"
                [pTooltip]="contract.contractObject"
                tooltipPosition="top">
                {{ contract.contractObject || '—' }}
              </span>
            </td>
            <td>{{ contract.endDate ? (contract.endDate | date:'dd/MM/yyyy') : '—' }}</td>
            <td class="text-center">
              @if (getDaysRemaining(contract.endDate) !== null) {
                <span
                  class="days-badge"
                  [ngClass]="getDaysRemainingClass(getDaysRemaining(contract.endDate))"
                  [attr.aria-label]="getDaysRemaining(contract.endDate) + ' giorni rimanenti alla scadenza'">
                  {{ getDaysRemaining(contract.endDate) }}
                </span>
              } @else {
                <span>—</span>
              }
            </td>
            <td class="text-center">
              {{ contract.renewalNoticeDays ? contract.renewalNoticeDays + ' gg' : '—' }}
            </td>
            <td class="text-center">
              <button
                pButton
                type="button"
                icon="pi pi-eye"
                severity="secondary"
                [text]="true"
                [rounded]="true"
                (click)="navigateToContract(contract.contractId)"
                [pTooltip]="'Apri contratto ' + contract.contractId"
                [attr.aria-label]="'Apri dettaglio contratto ' + contract.contractId">
              </button>
            </td>
          </tr>
        </ng-template>

        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="7">
              <div class="empty-state" role="status">
                <i class="pi pi-check-circle" aria-hidden="true"></i>
                <p>Nessuna attività pendente con i filtri selezionati</p>
              </div>
            </td>
          </tr>
        </ng-template>

        <ng-template pTemplate="loadingbody">
          <tr>
            <td colspan="7">
              <div class="loading-state" role="status" aria-live="polite">
                <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
                <span>Caricamento attività…</span>
              </div>
            </td>
          </tr>
        </ng-template>

      </p-table>
    </section>

  </main>

  <!-- Modale: lista fornitori per azienda selezionata -->
  <p-dialog
    [header]="'Fornitori — ' + companyModalLabel()"
    [(visible)]="companyModalVisible"
    [modal]="true"
    [draggable]="false"
    [resizable]="false"
    [style]="{ width: '480px', maxWidth: '95vw' }"
    [breakpoints]="{ '575px': '95vw' }"
    styleClass="company-suppliers-modal">

    <ul class="modal-supplier-list" role="list">
      @for (name of companyModalSuppliers(); track name) {
        <li>{{ name }}</li>
      }
      @if (!companyModalSuppliers().length) {
        <li class="empty-modal">Nessun fornitore trovato</li>
      }
    </ul>

    <ng-template pTemplate="footer">
      <button
        pButton
        type="button"
        label="Chiudi"
        icon="pi pi-times"
        severity="secondary"
        (click)="companyModalVisible = false"
        aria-label="Chiudi finestra fornitori">
      </button>
    </ng-template>
  </p-dialog>

</div>
